import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { SUBMIT_LEAD_GQL } from '../../Helpers/submission';
import { getPreSubmissionComponents } from '../__selectors__/component.selectors';
import SubmissionStatus from '../../Data/SubmissionStatus';
import submitBsaLead from '../../Helpers/submitBsaLead';
import {
  getStateAwareLegalStatement,
  selectCustomerStateCode,
} from '../LegalStatement/legalStatement.helpers';
import {
  getComponentsBeforePage,
  getPageNumbersWithPhoneQuestion,
} from '../../Helpers/components';
import isPhoneStepConversionEnabled from '../../Helpers/phoneStepConversion';
import selectIsPolarisFeatureEnabled from '../../Helpers/polaris';
import { ComponentTypes } from '../../Data/Components';
import SagaStatus from '../../Data/SagaStatus';

const getSupplierMatchComponents = (components) =>
  Object.fromEntries(
    Object.entries(components).filter(
      ([, component]) =>
        component.component.type === ComponentTypes.SUPPLIER_MATCH
    )
  );

/* Performance Impact:
 * The benefit of this wrapper component is to shield <Form /> and its other child
 * components from unnecessary re-runs whenever this self-managed component
 * re-renders. It is considered self-managed because all of its data dependencies
 * originate from the store and not from a parent component.
 */
const BsaLead = () => {
  const { useCidFromURL, widgetLabel, testMode, iFrameId } = useSelector(
    (store) => store.optionalFormConfig
  );
  const oneTrustLocale = useSelector((store) => store.oneTrustLocale);
  const {
    countryCode,
    phoneNumberCountryCode,
    formId,
    campaignId,
    sourceSite,
    pageUrl,
    referrerUrl,
    supplierId,
    privacyPolicy,
    submissionPageNumber,
    subcategoryName,
    subcategoryId,
    isDomainInternal,
    featureFlags,
  } = useSelector((store) => store.formConfig);
  const { consentCertification } = useSelector((store) => store.trustedForm);
  const legalStatement = useSelector((store) => store.legalStatement);
  const { consentedSuppliersHash } = legalStatement;
  const customerStateCode = useSelector(selectCustomerStateCode);
  const {
    statement: consentStatement,
    consentedLegalStatementHash,
    supplierLegalStatements,
  } = getStateAwareLegalStatement({ legalStatement, customerStateCode });
  const components = useSelector((store) => store.components);
  const {
    firstSubmissionAttemptTimestamp,
    bsaSubmissionStatus,
    submissionBlocks,
    submissionStatus,
  } = useSelector((store) => store.submission);
  const { parentPageCookies } = useSelector((store) => store.cookies);
  const {
    supplierMatchSelectionEnabled,
    customerMatches,
    subcategoryMatches,
    matchLevel,
    customerMatchesRequestStatus,
  } = useSelector((store) => store.supplierMatches);
  const { visitorId } = useSelector((store) => store.userIdentities);
  const { themeName } = useSelector((store) => store.theme);
  const oneToOneSupplierConsent = useSelector((store) =>
    selectIsPolarisFeatureEnabled(store, 'oneToOneSupplierConsent')
  );
  const captureBsaLeadsOnAbandonedFlows = useSelector((store) =>
    selectIsPolarisFeatureEnabled(store, 'captureBsaLeadsOnAbandonedFlows')
  );
  const dynamicSelectAndConnect = useSelector(
    (store) => store.features?.dynamicSelectAndConnect
  );
  const eventPublicKeyString = useSelector(
    (store) => store.publicKeys?.eventPublicKeyString
  );
  const currentPage = useSelector((store) => store.navigation?.currentPage);
  const {
    multipleSupplierConsentedLegalStatementHash,
    singleSupplierConsentedLegalStatementHash,
  } = supplierLegalStatements;
  const isSubmissionBlocked = submissionBlocks.length > 0;
  const isAwaitingCustomerMatches =
    customerMatchesRequestStatus === SagaStatus.PENDING;
  // Latched so submission retries resetting the status to NOT_STARTED do not
  // release a held BSA lead while the main lead is still being sent.
  const hasMainSubmissionStartedRef = useRef(false);
  if (submissionStatus !== SubmissionStatus.NOT_STARTED) {
    hasMainSubmissionStartedRef.current = true;
  }
  // A first attempt triggered in the same batch as the main submission is still
  // sent. Retries and leads held for customer matches are dropped once the main
  // submission starts.
  const wasHeldForCustomerMatchesRef = useRef(false);
  const hasAttemptedBsaLeadRef = useRef(false);
  const hasMainSubmissionStarted = hasMainSubmissionStartedRef.current;
  const [submitLeadMutation] = useMutation(SUBMIT_LEAD_GQL);
  const dispatch = useDispatch();
  const preSubmissionComponents = getPreSubmissionComponents(
    components,
    submissionPageNumber
  );

  const privacyPolicyUrl = privacyPolicy?.link || '';

  const shouldFirePhoneStepConversion = isPhoneStepConversionEnabled({
    oneToOneSupplierConsent,
    captureBsaLeadsOnAbandonedFlows,
  });

  useEffect(() => {
    const isAwaitingBsaSubmission =
      bsaSubmissionStatus === SubmissionStatus.AWAITING_BSA_SUBMISSION ||
      bsaSubmissionStatus === SubmissionStatus.AWAITING_RETRY_BSA_SUBMISSION;

    if (isAwaitingBsaSubmission && isAwaitingCustomerMatches) {
      wasHeldForCustomerMatchesRef.current = true;
    }
    const isUnheldFirstAttempt =
      bsaSubmissionStatus === SubmissionStatus.AWAITING_BSA_SUBMISSION &&
      !wasHeldForCustomerMatchesRef.current &&
      !hasAttemptedBsaLeadRef.current;

    if (
      !isAwaitingBsaSubmission ||
      isSubmissionBlocked ||
      isAwaitingCustomerMatches ||
      (hasMainSubmissionStarted && !isUnheldFirstAttempt)
    ) {
      return;
    }

    const phonePages = getPageNumbersWithPhoneQuestion(preSubmissionComponents);
    const phonePage = phonePages[phonePages.length - 1];

    if (phonePage === undefined) {
      return;
    }

    const componentsUpToPhonePage = getComponentsBeforePage(
      preSubmissionComponents,
      phonePage
    );
    hasAttemptedBsaLeadRef.current = true;
    submitBsaLead({
      campaignId,
      components: componentsUpToPhonePage,
      consentedLegalStatementHash,
      consentedSuppliersHash,
      countryCode,
      dispatch,
      formId,
      oneTrustLocale,
      pageUrl,
      parentPageCookies,
      phoneNumberCountryCode,
      referrerUrl,
      sourceSite,
      submitLeadMutation,
      testMode,
      useCidFromURL,
      widgetLabel,
      multipleSupplierConsentedLegalStatementHash,
      singleSupplierConsentedLegalStatementHash,
      consentCertification,
      supplierId,
      visitorId,
      consentStatement,
      privacyPolicyUrl,
      subcategoryName,
      firstSubmissionAttemptTimestamp,
      subcategoryId,
      supplierMatchSelectionEnabled,
      isDomainInternal,
      themeName,
      featureFlags,
      oneToOneSupplierConsent,
      shouldFirePhoneStepConversion,
      eventPublicKeyString,
      iFrameId,
      submissionPageNumber,
      currentPage,
      supplierMatchComponents: getSupplierMatchComponents(
        preSubmissionComponents
      ),
      customerBrandMatches: customerMatches,
      subcategoryBrandMatches: subcategoryMatches,
      matchLevel,
      dynamicSelectAndConnect,
    });
  }, [
    bsaSubmissionStatus,
    isSubmissionBlocked,
    isAwaitingCustomerMatches,
    hasMainSubmissionStarted,
  ]);

  return null;
};

export default BsaLead;
